import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ViewToken } from 'react-native';
import { ColorValues, Flex, List } from '@banhala/ui-kit';
import type { MarketCarouselEntity, MarketCarouselProps } from './types';
import { TwoImageHeader } from './components/TwoImageHeader';
import { MarketCard } from './components/MarketCard';
import { FavoriteButton } from './components/FavoriteButton';
import { isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const CARD_WIDTH = 202;
const INFO_HEIGHT = 68;

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

const keyExtractor = (_item: MarketCarouselEntity, index: number) =>
  index.toString();

const TwoImageCard = ({ item, logging }: MarketCarouselEntity) => {
  const { sno, profile, name, description, goods_images, favorite } = item;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePressCard = useCallback(() => {
    logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: logging.analytics,
      ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
    });
    openDeeplink(`ably://markets/${sno}/`);
  }, [openDeeplink, sno, logging, logAnalyticsEvent]);

  return (
    <View style={styles.cardPadding}>
      <View style={styles.cardShadow}>
        <Flex width={CARD_WIDTH} overflow="hidden" onPress={handlePressCard}>
          <TwoImageHeader images={goods_images} cardWidth={CARD_WIDTH} />

          <MarketCard
            sno={sno}
            profile={profile}
            name={name}
            description={description}
            logging={logging}
            profileSize={44}
            nameType="subtitle2"
            descriptionType="body4"
            descriptionColor="content_tertiary"
            height={INFO_HEIGHT}
            paddingHorizontal={8}
            paddingVertical={12}
            textGap={4}
            pressable={false}
            showChevron={true}
          />

          <View style={styles.favoriteContainer}>
            <FavoriteButton
              sno={sno}
              isFavorited={favorite?.is_favorited ?? false}
              logging={logging}
              uncheckedColor={ColorValues.white}
            />
          </View>
        </Flex>
      </View>
    </View>
  );
};

const renderItem = ({ item }: { item: MarketCarouselEntity }) => (
  <TwoImageCard item={item.item} logging={item.logging} />
);

export const TwoImageMarketCarousel = ({
  entity,
  isVisible,
  isAwaitingDataChange,
}: MarketCarouselProps) => {
  // GoodsCarousel.useLogDisplayGoods 패턴 — inner FlatList 가 부모(AtomicRenderer)
  // viewport 와 무관하게 viewable 을 보고하므로, emit 시점에 부모 가시성으로 한번 더
  // 가드 + isVisible false→true 전이 시 캐시 기반 재emit 으로 노출 결손 방지.
  const loggedSnosRef = useRef<Set<number>>(new Set());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (isVisibleRef.current === false) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable || isNil(item)) return;
        const entityItem = item as MarketCarouselEntity;
        const sno = entityItem.item.sno;
        if (loggedSnosRef.current.has(sno)) return;
        loggedSnosRef.current.add(sno);
        logAnalyticsEvent({
          name: 'DISPLAY_MARKET',
          analytics: {
            ...entityItem.logging.analytics,
            IS_FAVORITED: entityItem.item.favorite?.is_favorited ? 1 : 0,
          },
          ably_only_log: entityItem.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  // isVisible truthy 로 전이되거나 awaiting 윈도우 해제 시점에 캐시 기반 재평가.
  // loggedSnos dedup 으로 중복 emit 없이 시야 진입 시점의 viewable 만 보냄.
  useEffect(() => {
    if (isVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 12,
    paddingBottom: 24,
    gap: 8,
  },
  cardPadding: {
    padding: 1,
  },
  cardShadow: {
    backgroundColor: ColorValues.white,
    elevation: 1,
    shadowColor: ColorValues.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
  },
  favoriteContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});
